import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';

export const formatCurrency = (value: number, currency = 'IDR') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
};

export const exportToExcel = <T extends Record<string, any>>(
  data: T[],
  filename: string,
  sheetName: string = 'Sheet1'
) => {
  if (!data || data.length === 0) return;

  const headers = Object.keys(data[0]);
  
  const xmlHeader = `<?xml version="1.0" encoding="UTF-8"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
  xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
  <Worksheet ss:Name="${sheetName}">
    <Table>`;

  const xmlFooter = `</Table>
  </Worksheet>
</Workbook>`;

  const headerRow = `<Row>${headers.map(h => 
    `<Cell><Data ss:Type="String">${escapeXml(String(h))}</Data></Cell>`
  ).join('')}</Row>`;

  const dataRows = data.map(row => 
    `<Row>${headers.map(h => {
      const value = row[h];
      const type = typeof value === 'number' ? 'Number' : 'String';
      const escaped = escapeXml(String(value ?? ''));
      return `<Cell><Data ss:Type="${type}">${escaped}</Data></Cell>`;
    }).join('')}</Row>`
  ).join('\n');

  const xml = xmlHeader + headerRow + dataRows + xmlFooter;
  
  const blob = new Blob([xml], { type: 'application/vnd.ms-excel;charset=utf-8' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}_${format(new Date(), 'yyyy-MM-dd')}.xls`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

function escapeXml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

export const exportToCSV = (data: any[], filename: string, headers?: string[]) => {
  if (!data || data.length === 0) return;

  const headerKeys = headers || Object.keys(data[0]);
  const csvContent = [
    headerKeys.join(','),
    ...data.map((row) =>
      headerKeys.map((key) => JSON.stringify(row[key] ?? '')).join(',')
    ),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}_${format(new Date(), 'yyyy-MM-dd')}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const exportToPDF = (
  data: any[],
  title: string,
  columns: Array<{ header: string; dataKey: string }>,
  options?: {
    filename?: string;
    footerText?: string;
    summary?: { label: string; value: string | number }[];
  }
) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  doc.setFontSize(18);
  doc.setTextColor(37, 99, 235);
  doc.text(title, pageWidth / 2, 20, { align: 'center' });
  
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(`Generated: ${format(new Date(), 'PPpp')}`, pageWidth / 2, 28, { align: 'center' });
  
  const tableData = data.map((row) =>
    columns.map((col) => {
      const value = row[col.dataKey];
      if (value === null || value === undefined) return '-';
      if (typeof value === 'number') {
        return col.dataKey.toLowerCase().includes('value') || 
               col.dataKey.toLowerCase().includes('amount') ||
               col.dataKey.toLowerCase().includes('price') ||
               col.dataKey.toLowerCase().includes('cost') ||
               col.dataKey.toLowerCase().includes('total')
          ? formatCurrency(value)
          : value.toLocaleString();
      }
      return String(value);
    })
  );

  autoTable(doc, {
    head: [columns.map((col) => col.header)],
    body: tableData,
    startY: 38,
    theme: 'striped',
    headStyles: {
      fillColor: [37, 99, 235],
      textColor: 255,
      fontStyle: 'bold',
    },
    styles: {
      fontSize: 9,
      cellPadding: 4,
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },
  });

  let finalY = (doc as any).lastAutoTable.finalY || 100;

  if (options?.summary && options.summary.length > 0) {
    finalY += 10;
    doc.setFontSize(12);
    doc.setTextColor(37, 99, 235);
    doc.text('Summary', 14, finalY);
    
    finalY += 8;
    options.summary.forEach((item, index) => {
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`${item.label}:`, 14, finalY + index * 6);
      doc.setTextColor(37, 99, 235);
      doc.text(String(item.value), 80, finalY + index * 6);
    });
    
    finalY += options.summary.length * 6 + 10;
  }

  if (options?.footerText) {
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(options.footerText, pageWidth / 2, finalY + 20, { align: 'center' });
  }

  doc.setFontSize(8);
  doc.setTextColor(150);
  doc.text(
    'Hospital Inventory Management System',
    pageWidth / 2,
    doc.internal.pageSize.getHeight() - 10,
    { align: 'center' }
  );

  doc.save(`${options?.filename || title.replace(/\s+/g, '_')}_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
};

export const exportInventoryValuationToPDF = (data: {
  total_value: number;
  items: Array<{
    item_name: string;
    item_code: string;
    location_name: string;
    quantity: number;
    unit_cost: number;
    total_value: number;
  }>;
}) => {
  const columns = [
    { header: 'Item', dataKey: 'item_name' },
    { header: 'Code', dataKey: 'item_code' },
    { header: 'Location', dataKey: 'location_name' },
    { header: 'Quantity', dataKey: 'quantity' },
    { header: 'Unit Cost', dataKey: 'unit_cost' },
    { header: 'Total Value', dataKey: 'total_value' },
  ];

  exportToPDF(data.items, 'Inventory Valuation Report', columns, {
    filename: 'inventory_valuation',
    footerText: 'This report shows the current valuation of inventory items across all locations.',
    summary: [
      { label: 'Total Inventory Value', value: formatCurrency(data.total_value) },
      { label: 'Total Items', value: data.items.length },
    ],
  });
};

export const exportPurchaseAnalyticsToPDF = (data: {
  grand_total: number;
  spending_by_vendor: Array<{
    vendor_name: string;
    vendor_code: string;
    total_spent: number;
    po_count: number;
    percentage: string;
  }>;
  summary: { total_vendors: number; total_pos: number };
}) => {
  const columns = [
    { header: 'Vendor', dataKey: 'vendor_name' },
    { header: 'Code', dataKey: 'vendor_code' },
    { header: 'Total Spent', dataKey: 'total_spent' },
    { header: 'PO Count', dataKey: 'po_count' },
    { header: '% of Total', dataKey: 'percentage' },
  ];

  exportToPDF(data.spending_by_vendor, 'Purchase Analytics Report', columns, {
    filename: 'purchase_analytics',
    footerText: 'This report shows spending analysis by vendor.',
    summary: [
      { label: 'Grand Total', value: formatCurrency(data.grand_total) },
      { label: 'Total Vendors', value: data.summary.total_vendors },
      { label: 'Total POs', value: data.summary.total_pos },
    ],
  });
};

export const exportPurchaseForecastToPDF = (data: {
  total_recommended_value: number;
  period_months: number;
  items: Array<{
    item_name: string;
    item_code: string;
    current_stock: number;
    estimated_monthly_need: number;
    recommended_order_qty: number;
    urgency: string;
  }>;
}) => {
  const columns = [
    { header: 'Item', dataKey: 'item_name' },
    { header: 'Code', dataKey: 'item_code' },
    { header: 'Current Stock', dataKey: 'current_stock' },
    { header: 'Monthly Need', dataKey: 'estimated_monthly_need' },
    { header: 'Recommended Qty', dataKey: 'recommended_order_qty' },
    { header: 'Urgency', dataKey: 'urgency' },
  ];

  exportToPDF(data.items, 'Purchase Forecast Report', columns, {
    filename: 'purchase_forecast',
    footerText: `Forecast period: ${data.period_months} months`,
    summary: [
      { label: 'Total Recommended Value', value: formatCurrency(data.total_recommended_value) },
      { label: 'Items to Order', value: data.items.length },
    ],
  });
};
